import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';
import { CompanyService } from 'app/services/company.service';
import { VehicleService } from 'app/services/vehicle.service';
import { VehicleModel } from 'app/models/vehicle.model';
import { CompanyModel } from 'app/models/company.model';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';

/**
 * Cabeçalho do cockpit: troca de ambulância ao lado do menu, mesmo conceito da listagem de projetos
 * (setas anterior/próximo + dropdown com lista da mesma empresa).
 */
@Component({
    selector: 'app-cockpit-vehicle-header',
    standalone: true,
    imports: [NgIf, NgFor, MatButtonModule, MatIconModule, MatMenuModule, AutoTranslateDirective],
    templateUrl: './cockpit-vehicle-header.component.html',
    styleUrls: ['./cockpit-vehicle-header.component.scss'],
})
export class CockpitVehicleHeaderComponent implements OnInit, OnDestroy {
    /** Só mostra em /cockpit/:id */
    visible = false;
    currentVehicleId: number | null = null;
    vehicle: VehicleModel | null = null;
    vehicles: VehicleModel[] = [];
    company: CompanyModel | null = null;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private vehicleService: VehicleService,
        private companyService: CompanyService,
        private _cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.companyService.currentCompany$
            .pipe(
                distinctUntilChanged((a, b) => a?.company_id === b?.company_id),
                takeUntil(this._destroy$),
            )
            .subscribe((c) => {
                this.company = c;
                if (this.visible && this.currentVehicleId != null && c?.company_id) {
                    void this.loadForVehicle(this.currentVehicleId);
                }
                this._cdr.markForCheck();
            });

        const sync = () => {
            const id = this.parseCockpitVehicleId();
            const nextVisible = id != null;
            if (this.visible !== nextVisible || this.currentVehicleId !== id) {
                this.visible = nextVisible;
                this.currentVehicleId = id;
                if (this.visible && id != null) {
                    void this.loadForVehicle(id);
                } else {
                    this.vehicle = null;
                    this.vehicles = [];
                }
                this._cdr.markForCheck();
            }
        };

        sync();
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd), takeUntil(this._destroy$)).subscribe(() => sync());
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private parseCockpitVehicleId(): number | null {
        const m = this.router.url.split('?')[0].match(/\/cockpit\/(\d+)/);
        if (!m) {
            return null;
        }
        const n = Number(m[1]);
        return Number.isFinite(n) ? n : null;
    }

    private async loadForVehicle(vehicleId: number): Promise<void> {
        try {
            this.vehicle = await this.vehicleService.getById(vehicleId).promise;
        } catch {
            this.vehicle = null;
        }
        await this.loadCompanyVehicles();
        this._cdr.detectChanges();
    }

    private async loadCompanyVehicles(): Promise<void> {
        if (!this.company?.company_id) {
            this.vehicles = [];
            return;
        }
        try {
            const res = await this.vehicleService
                .list({ company_id: this.company.company_id, limit: 'all' } as any)
                .promise;
            const items: VehicleModel[] = res?.items || [];
            this.vehicles = items
                .slice()
                .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        } catch {
            this.vehicles = [];
        }
        if (this.vehicles.length === 0 && this.vehicle) {
            this.vehicles = [this.vehicle];
        }
    }

    get displayName(): string {
        if (!this.vehicle) {
            return this.currentVehicleId != null ? `#${this.currentVehicleId}` : '—';
        }
        if (this.vehicle.name?.trim()) {
            return this.vehicle.name;
        }
        if (this.vehicle.license_plate?.trim()) {
            return this.vehicle.license_plate;
        }
        return String(this.vehicle.vehicle_id);
    }

    get plateLine(): string {
        return this.vehicle?.license_plate?.trim() || '—';
    }

    get groupLine(): string {
        return this.vehicle?.group?.trim() || '—';
    }

    get currentIndex(): number {
        if (this.currentVehicleId == null) {
            return -1;
        }
        return this.vehicles.findIndex((v) => v.vehicle_id === this.currentVehicleId);
    }

    get canGoPrev(): boolean {
        return this.currentIndex > 0;
    }

    get canGoNext(): boolean {
        return this.currentIndex >= 0 && this.currentIndex < this.vehicles.length - 1;
    }

    goPrev(): void {
        if (!this.canGoPrev) {
            return;
        }
        const v = this.vehicles[this.currentIndex - 1];
        this.navigateTo(v.vehicle_id);
    }

    goNext(): void {
        if (!this.canGoNext) {
            return;
        }
        const v = this.vehicles[this.currentIndex + 1];
        this.navigateTo(v.vehicle_id);
    }

    selectVehicle(v: VehicleModel): void {
        if (v.vehicle_id === this.currentVehicleId) {
            return;
        }
        this.navigateTo(v.vehicle_id);
    }

    private navigateTo(vehicleId: number): void {
        this.router.navigate(['/cockpit', vehicleId], { replaceUrl: true });
    }

    trackByVehicleId(_: number, v: VehicleModel): number {
        return v.vehicle_id;
    }
}
